import { create } from 'zustand'
import {
  api,
  alCambiarLaApi,
  cerrarSesionLocal,
  esFallaDeConexion,
  fijarSlug,
  iniciarSesionCaja,
  iniciarSesionLocal,
  sesionCajaGuardada,
  sesionGuardada,
} from './api'
import { esCaja } from './config'
import { fijarZonaDelLocal } from './formato'
import { olvidarSuplantada, sesionSuplantada } from './suplantacion'

// Estado de la sesión. Lo que el resto de la app consulta para saber quién
// está adentro y qué puede hacer.

export interface UsuarioSesion {
  id: number
  nombre: string
  email: string
  rol: string
  permisos: string[]
  iniciales: string
  fotoUrl?: string
}

/** Un empleado de la lista de la pantalla de PIN de la caja. */
export interface UsuarioParaPin {
  id: number
  nombre: string
  rol: string
}

export interface TenantPublico {
  slug: string
  nombre: string
  logoUrl?: string
  zonaHoraria: string

  /**
   * Si el sistema puede mandar el mail de «olvidé mi contraseña».
   *
   * Lo resuelve el servidor (necesita el correo configurado y una dirección
   * pública con la que armar el enlace) y llega en la misma respuesta que el
   * nombre y el logo. Opcional porque la caja del mostrador arma este objeto
   * con lo que tiene guardado, sin preguntarle a la nube.
   */
  recuperacionDisponible?: boolean
}

interface RespuestaLogin {
  accessToken: string
  refreshToken: string
  accessTokenExpiraAt: string
  usuario: UsuarioSesion
  tenant: TenantPublico
}

interface EstadoSesion {
  usuario: UsuarioSesion | null
  tenant: TenantPublico | null
  /** True mientras se intenta rehidratar la sesión guardada al arrancar. */
  cargando: boolean

  entrar: (slug: string, email: string, password: string) => Promise<void>
  entrarConPin: (
    slug: string,
    usuarioId: number,
    pin: string,
    instalacionId: string,
  ) => Promise<void>

  /**
   * Entrada en la caja: el cajero elige su nombre y pone el PIN.
   *
   * Va contra el host LOCAL, no contra la nube: es lo que hace que abrir la
   * caja funcione con el módem caído, que es justo cuando más falta hace. El
   * host valida contra la nube si puede y contra el hash cacheado si no.
   */
  entrarEnLaCaja: (usuarioId: number, pin: string) => Promise<void>
  salir: () => Promise<void>
  rehidratar: () => Promise<void>
  /** Aplica una sesión ya obtenida (la usa el refresh). */
  aplicar: (r: RespuestaLogin) => void

  /** Si el usuario tiene el permiso. Nunca se chequea por rol. */
  puede: (permiso: string) => boolean
}

export const useSesion = create<EstadoSesion>((set, get) => ({
  usuario: null,
  tenant: null,
  cargando: true,

  aplicar: (r) => {
    // Las fechas se muestran en la zona del LOCAL y no en la del navegador
    // (§1.8): la PC del mostrador puede estar en UTC y la grilla mostraría
    // otra hora que el ticket impreso. Ver lib/formato.
    fijarZonaDelLocal(r.tenant.zonaHoraria)

    iniciarSesionLocal({
      accessToken: r.accessToken,
      refreshToken: r.refreshToken,
      slug: r.tenant.slug,
    })
    set({ usuario: r.usuario, tenant: r.tenant, cargando: false })
  },

  entrar: async (slug, email, password) => {
    const r = await api.login<RespuestaLogin>('/auth/login', {
      tenantSlug: slug,
      email,
      password,
    })
    get().aplicar(r)
  },

  entrarConPin: async (slug, usuarioId, pin, instalacionId) => {
    // El usuarioId es OBLIGATORIO: el cajero toca su nombre y después pone el
    // PIN. Faltaba, y por eso la entrada con PIN desde el navegador nunca podía
    // validar: el servidor verifica el hash del usuario QUE SE ELIGIÓ, no
    // busca al dueño del PIN (ver LoginPinRequest.UsuarioId).
    const r = await api.login<RespuestaLogin>('/auth/login-pin', {
      tenantSlug: slug,
      usuarioId,
      pin,
      instalacionId,
    })
    get().aplicar(r)
  },

  entrarEnLaCaja: async (usuarioId, pin) => {
    const r = await api.login<RespuestaSesionCaja>('/auth/pin', { usuarioId, pin }, 'local')

    const slug = r.local?.slug || r.usuario.local || sesionGuardada().tenantSlug || ''

    iniciarSesionCaja(r.token, slug)

    set({
      usuario: {
        id: r.usuario.id,
        nombre: r.usuario.nombre,
        email: '',
        rol: '',
        permisos: r.usuario.permisos,
        iniciales: iniciales(r.usuario.nombre),
      },

      // El nombre del local sale de lo que la CAJA tiene guardado y no de
      // `/auth/tenant/{slug}`, que es un endpoint de la NUBE: el host local no
      // lo tiene, así que la consulta fallaba y la barra lateral mostraba un
      // guión donde va el nombre del negocio.
      //
      // La zona es la de la MÁQUINA, igual que al rehidratar: la caja está
      // físicamente en el local, así que su reloj es el del mostrador (la
      // misma decisión que toma el ticket impreso). Y no puede quedar vacía:
      // de la zona de la sesión sale el «hoy» del local (ver lib/rango).
      tenant: r.local
        ? { slug: r.local.slug, nombre: r.local.nombre, zonaHoraria: zonaDeLaMaquina() }
        : null,

      cargando: false,
    })
  },

  salir: async () => {
    const { refreshToken } = sesionGuardada()

    // Se avisa al servidor para que revoque el refresh token, pero el estado
    // local se limpia igual si el request falla: quedarse "adentro" porque no
    // había internet es peor que no poder revocar.
    try {
      if (refreshToken) await api.post('/auth/logout', { refreshToken })
    } catch {
      /* se limpia igual */
    }

    cerrarSesionLocal()
    olvidarSuplantada()
    set({ usuario: null, tenant: null })
  },

  // Se rehidrata UNA sola vez por carga de página, aunque la llamen varias.
  //
  // Esto no es una micro-optimización: el refresh rota el token y el
  // servidor trata el segundo canje del mismo token como posible robo. Dos
  // llamadas concurrentes (el doble montaje de StrictMode en desarrollo, dos
  // componentes que rehidratan, un StrictMode futuro en producción) mandaban
  // el mismo token dos veces y el resultado era que recargar la página
  // deslogueaba al empleado. El servidor además tiene una ventana de gracia,
  // pero el cliente no tiene por qué gastarla.
  rehidratar: () => {
    // En la caja la sesión es el token opaco del host local: se rehidrata de
    // sessionStorage para que un F5 accidental en la pantalla táctil no
    // obligue a poner el PIN de nuevo. Ver iniciarSesionCaja.
    if (esCaja()) {
      rehidratacion ??= rehidratarCaja(set).finally(() => {
        rehidratacion = null
      })

      return rehidratacion
    }

    rehidratacion ??= rehidratarUnaVez(set, get).finally(() => {
      // Se libera para que un `salir()` seguido de otra rehidratación (por
      // ejemplo al volver a entrar) no quede pegado al resultado anterior.
      rehidratacion = null
    })

    return rehidratacion
  },

  puede: (permiso) => get().usuario?.permisos.includes(permiso) ?? false,
}))

let rehidratacion: Promise<void> | null = null

async function rehidratarUnaVez(
  set: (parcial: Partial<EstadoSesion>) => void,
  get: () => EstadoSesion,
): Promise<void> {
  // Primero la sesión de alguien de PLATAFORMA que entró al local.
  //
  // Va antes del refresh porque esa sesión NO tiene refresh token —muere a los
  // treinta minutos y hay que volver a pedirla, con su motivo y su registro—,
  // así que por el camino normal un F5 la echaría al login del local, donde
  // esa persona no tiene contraseña.
  const suplantada = sesionSuplantada()

  if (suplantada) {
    fijarZonaDelLocal(suplantada.tenant.zonaHoraria)

    iniciarSesionLocal({
      accessToken: suplantada.accessToken,
      refreshToken: '',
      slug: suplantada.tenantSlug,
    })

    set({ usuario: suplantada.usuario, tenant: suplantada.tenant, cargando: false })
    return
  }

  const { refreshToken, tenantSlug } = sesionGuardada()

  if (!refreshToken) {
    set({ cargando: false })

    // Aunque no haya sesión, si se conoce el local se traen sus datos
    // públicos: la pantalla de login muestra su nombre y su logo en lugar de
    // un formulario genérico.
    if (tenantSlug) await cargarTenantEnEstado(tenantSlug, set)
    return
  }

  try {
    const r = await api.post<RespuestaLogin>('/auth/refresh', { refreshToken })
    cortarReintento()
    get().aplicar(r)
  } catch (e) {
    // Un corte de conexión NO invalida la sesión: el token sigue siendo bueno
    // y lo único que pasó es que no se pudo preguntar. Borrarlo dejaba al
    // cajero afuera por un corte —al volver internet tenía que entrar con mail
    // y contraseña, y en la tablet del mostrador se entra con PIN— y encima
    // pasa en el peor momento posible: al abrir el navegador a la mañana,
    // cuando el módem todavía no levantó.
    if (esFallaDeConexion(e)) programarReintento(get)
    else {
      cortarReintento()
      cerrarSesionLocal()
    }

    set({ usuario: null, cargando: false })
    if (tenantSlug) await cargarTenantEnEstado(tenantSlug, set)
  }
}

// ── Reintento de la rehidratación ───────────────────────────────────────────
//
// Mientras el problema sea de conexión se vuelve a intentar solo. La
// alternativa es un cajero mirando la pantalla de login sin saber que lo único
// que le falta es apretar F5, y en un local que trabaja todo en línea eso es
// el mostrador parado.
//
// El reintento va por `rehidratar()` y no llamando derecho a la función,
// porque ese es el único lugar con el candado que impide canjear el mismo
// refresh token dos veces a la vez: el servidor trata el segundo canje como
// posible robo y corta la sesión, así que un reintento sin candado sería peor
// que no reintentar.
let reintento: number | null = null

function programarReintento(get: () => EstadoSesion) {
  if (reintento !== null) return

  const intentar = () => {
    // Si alguien entró a mano mientras esperábamos, o cerró sesión, no hay
    // nada que rehidratar.
    if (get().usuario !== null || !sesionGuardada().refreshToken) {
      cortarReintento()
      return
    }

    void get().rehidratar()
  }

  // Cada cinco segundos y además en cuanto el navegador avisa que volvió la
  // red o que la API contesta: los tres avisos llegan tarde alguna vez, y el
  // único costo de tenerlos juntos es un pedido chico de más.
  reintento = window.setInterval(intentar, 5000)
  window.addEventListener('online', intentar)
  bajaDelAviso = alCambiarLaApi((responde) => {
    if (responde) intentar()
  })
  quitarEscucha = () => window.removeEventListener('online', intentar)
}

let quitarEscucha: (() => void) | null = null
let bajaDelAviso: (() => void) | null = null

function cortarReintento() {
  if (reintento !== null) {
    clearInterval(reintento)
    reintento = null
  }

  quitarEscucha?.()
  quitarEscucha = null
  bajaDelAviso?.()
  bajaDelAviso = null
}

/**
 * La zona horaria de esta máquina.
 *
 * Es la respuesta correcta en la caja del mostrador: la PC está en el local,
 * así que su reloj es el del mostrador, y es lo único que se puede saber sin
 * internet (la zona del local no baja en el contrato de sincronización).
 */
function zonaDeLaMaquina(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch {
    return 'UTC'
  }
}

// ── Rehidratación de la caja ────────────────────────────────────────────────

async function rehidratarCaja(set: (parcial: Partial<EstadoSesion>) => void): Promise<void> {
  const token = sesionCajaGuardada()

  if (!token) {
    set({ cargando: false })
    return
  }

  try {
    // Se verifica el token contra el host antes de darlo por bueno: pudo
    // vencer, o el proceso pudo reiniciarse (las sesiones de la caja viven en
    // memoria a propósito).
    const estado = await api.get<{ local?: string | null }>('/estado', { destino: 'local' })

    const yo = await api.get<{
      id: number
      nombre: string
      permisos: string[]
      local?: { slug: string; nombre: string } | null
    }>('/auth/yo', { destino: 'local' })

    set({
      usuario: {
        id: yo.id,
        nombre: yo.nombre,
        email: '',
        rol: '',
        permisos: yo.permisos,
        iniciales: iniciales(yo.nombre),
      },
      // La zona es la de la MÁQUINA, y no un literal: la caja está
      // físicamente en el local, así que su reloj es el del mostrador (es la
      // misma decisión que toma el ticket impreso, ver DatosTicketLocal). Y un
      // literal acá sería un país hardcodeado.
      //
      // El NOMBRE sale de lo que el host tiene guardado. Antes se usaba el
      // slug, así que después de recargar la barra lateral decía «laesquina»
      // en lugar de «Verdulería La Esquina»: el slug es una dirección, no el
      // nombre del negocio.
      tenant: yo.local
        ? { slug: yo.local.slug, nombre: yo.local.nombre, zonaHoraria: zonaDeLaMaquina() }
        : estado.local
          ? { slug: estado.local, nombre: estado.local, zonaHoraria: zonaDeLaMaquina() }
          : null,
      cargando: false,
    })
  } catch (e) {
    // Igual que en la nube: si no se pudo llegar al host —se está reiniciando
    // después de una actualización, por ejemplo— el token no se toca. Si el
    // host contestó que no vale, ahí sí se limpia y se pide el PIN.
    if (!esFallaDeConexion(e)) cerrarSesionLocal()
    set({ usuario: null, cargando: false })
  }
}

/**
 * Empleados habilitados para el PIN en un dispositivo registrado como caja.
 *
 * Es el equivalente en la NUBE de la lista que sirve el host local, para el
 * local que trabaja todo en línea: la tablet del mostrador cambia de cajero con
 * cuatro dígitos en lugar de tipear mail y contraseña en cada relevo.
 *
 * Se piden con el identificador de la instalación porque todavía no hay
 * sesión, y eso es lo que hace que la lista no quede a la vista de cualquiera
 * que conozca el nombre del local.
 */
export async function cargarUsuariosDeLaCajaWeb(
  slug: string,
  instalacionId: string,
): Promise<UsuarioParaPin[]> {
  return await api.login<UsuarioParaPin[]>('/auth/usuarios-caja', {
    tenantSlug: slug,
    instalacionId,
  })
}

/** Empleados habilitados para la caja. Sale del caché local, así que anda sin internet. */
export async function cargarUsuariosDeLaCaja(): Promise<UsuarioParaPin[]> {
  try {
    return await api.get<UsuarioParaPin[]>('/auth/usuarios', { destino: 'local' })
  } catch {
    return []
  }
}

function iniciales(nombre: string): string {
  const partes = nombre.split(' ').filter(Boolean)

  if (partes.length === 0) return '?'
  if (partes.length === 1) return partes[0]!.slice(0, 2).toUpperCase()

  return `${partes[0]![0]}${partes[partes.length - 1]![0]}`.toUpperCase()
}

interface RespuestaSesionCaja {
  token: string
  venceAt: string
  usuario: {
    id: number
    nombre: string
    permisos: string[]
    local?: string | null
  }

  /**
   * El local, con lo que la caja tiene guardado de la última vez que habló
   * con la nube. Opcional: un host viejo no lo manda.
   */
  local?: { slug: string; nombre: string } | null
}

async function cargarTenantEnEstado(
  slug: string,
  set: (parcial: Partial<EstadoSesion>) => void,
) {
  try {
    fijarSlug(slug)
    const tenant = await api.get<TenantPublico>(`/auth/tenant/${encodeURIComponent(slug)}`)
    fijarZonaDelLocal(tenant.zonaHoraria)
    set({ tenant })
  } catch (e) {
    // Slug desconocido o local deshabilitado: la pantalla de login pide el
    // nombre del local en lugar de mostrar uno equivocado. Pero si lo que
    // falló fue la conexión no se sabe nada del local, así que se deja lo que
    // ya se sabía: borrarlo hacía que la pantalla dijera que el local no
    // existe, que es mentira y manda a buscar el problema al lugar equivocado.
    if (!esFallaDeConexion(e)) set({ tenant: null })
  }
}

/** Carga los datos públicos de un local, para la pantalla de login. */
export async function cargarTenantPublico(slug: string): Promise<TenantPublico | null> {
  try {
    fijarSlug(slug)
    return await api.get<TenantPublico>(`/auth/tenant/${encodeURIComponent(slug)}`)
  } catch (e) {
    // `null` significa UNA sola cosa: el servidor contestó que ese local no
    // existe. La falla de conexión se propaga para que la pantalla pueda
    // decirlo como es; devolver `null` también en ese caso era lo que hacía
    // que un corte de internet se leyera como «no encontramos el local
    // laesquina», con el verdulero revisando la dirección que estaba bien.
    if (esFallaDeConexion(e)) throw e

    return null
  }
}
